/**
 * The field — one fullscreen fragment shader.
 *
 * A domain-warped simplex flow at very low spatial frequency, coloured by a
 * continuous ramp through the bloom palette and banded into the lower-right
 * over an abyss→deep ground. Every colour arrives as a uniform read from the
 * CSS custom properties, so the shader and the token layer cannot drift apart.
 */

export const VERT = `#version 300 es
in vec2 aPos;
out vec2 vUv;
void main() {
  vUv = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

export const FRAG = `#version 300 es
precision highp float;

in vec2 vUv;
out vec4 outColor;

uniform vec2  uRes;
uniform float uTime;
uniform vec2  uMouse;    // 0..1, damped
uniform float uWarp;     // 0.2 calm .. 2.6 turbulent
uniform float uHue;      // 0 = teal/peach, 1 = iris/amber
uniform float uBloom;    // bloom intensity
uniform float uLift;     // pushes the band up the page
uniform float uOpacity;  // 0 = flat abyss, 1 = full field

uniform vec3 uAbyss;
uniform vec3 uDeep;
uniform vec3 uCyan;
uniform vec3 uPeach;
uniform vec3 uAmber;
uniform vec3 uIris;

/* --- Ashima simplex 3D ------------------------------------------------- */
vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 permute(vec4 x) { return mod289(((x * 34.0) + 1.0) * x); }
vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

float snoise(vec3 v) {
  const vec2 C = vec2(1.0 / 6.0, 1.0 / 3.0);
  const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);

  vec3 i  = floor(v + dot(v, C.yyy));
  vec3 x0 = v - i + dot(i, C.xxx);

  vec3 g = step(x0.yzx, x0.xyz);
  vec3 l = 1.0 - g;
  vec3 i1 = min(g.xyz, l.zxy);
  vec3 i2 = max(g.xyz, l.zxy);

  vec3 x1 = x0 - i1 + C.xxx;
  vec3 x2 = x0 - i2 + C.yyy;
  vec3 x3 = x0 - D.yyy;

  i = mod289(i);
  vec4 p = permute(permute(permute(
             i.z + vec4(0.0, i1.z, i2.z, 1.0))
           + i.y + vec4(0.0, i1.y, i2.y, 1.0))
           + i.x + vec4(0.0, i1.x, i2.x, 1.0));

  float n_ = 0.142857142857;
  vec3 ns = n_ * D.wyz - D.xzx;

  vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
  vec4 x_ = floor(j * ns.z);
  vec4 y_ = floor(j - 7.0 * x_);

  vec4 x = x_ * ns.x + ns.yyyy;
  vec4 y = y_ * ns.x + ns.yyyy;
  vec4 h = 1.0 - abs(x) - abs(y);

  vec4 b0 = vec4(x.xy, y.xy);
  vec4 b1 = vec4(x.zw, y.zw);

  vec4 s0 = floor(b0) * 2.0 + 1.0;
  vec4 s1 = floor(b1) * 2.0 + 1.0;
  vec4 sh = -step(h, vec4(0.0));

  vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy;
  vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;

  vec3 p0 = vec3(a0.xy, h.x);
  vec3 p1 = vec3(a0.zw, h.y);
  vec3 p2 = vec3(a1.xy, h.z);
  vec3 p3 = vec3(a1.zw, h.w);

  vec4 norm = taylorInvSqrt(vec4(dot(p0, p0), dot(p1, p1), dot(p2, p2), dot(p3, p3)));
  p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;

  vec4 m = max(0.6 - vec4(dot(x0, x0), dot(x1, x1), dot(x2, x2), dot(x3, x3)), 0.0);
  m = m * m;
  return 42.0 * dot(m * m, vec4(dot(p0, x0), dot(p1, x1), dot(p2, x2), dot(p3, x3)));
}

/* Two octaves, and the second is deliberately quiet. This field is made of very
   large soft shapes — more octaves turn it into marble. */
float fbm(vec3 p) {
  float v = snoise(p);
  v += 0.42 * snoise(p * 2.1);
  v += 0.16 * snoise(p * 4.3);
  return v * 0.62;
}

/* Continuous palette ramp. Colour comes from one smooth scalar walking the
   bloom stops, which keeps regions large instead of speckling per pixel. */
vec3 ramp(float x, vec3 a, vec3 b, vec3 c, vec3 d) {
  x = clamp(x, 0.0, 1.0);
  vec3 col = mix(a, b, smoothstep(0.00, 0.36, x));
  col = mix(col, c, smoothstep(0.32, 0.68, x));
  col = mix(col, d, smoothstep(0.64, 1.00, x));
  return col;
}

/* Interleaved gradient noise — one-line ordered dither. Without it the ramp
   bands visibly on 8-bit displays, which is what makes a gradient read as CSS
   rather than as film. */
float ign(vec2 p) {
  return fract(52.9829189 * fract(dot(p, vec2(0.06711056, 0.00583715))));
}

void main() {
  vec2 uv = vUv;
  float aspect = uRes.x / max(uRes.y, 1.0);
  vec2 p = (uv - 0.5) * vec2(aspect, 1.0);

  float t = uTime * 0.030;

  /* The bloom leans toward the pointer without chasing it */
  vec2 m = (uMouse - 0.5) * vec2(aspect, 1.0);
  p += (p - m) * 0.06 * uWarp;

  /* One round of domain warping at a very low frequency. The shapes want to be
     the size of the viewport, not the size of a brushstroke. */
  vec3 sp = vec3(p * 0.42, t);
  vec2 q = vec2(fbm(sp), fbm(sp + vec3(2.8, 1.4, 0.0)));
  float n = fbm(sp + vec3(q * 0.85 * uWarp, 0.10 * t) + vec3(1.7, 9.2, 0.0));

  /* Ground ramp — the top of the frame stays near-abyss so type sits on ink */
  vec3 base = mix(uAbyss, uDeep, smoothstep(1.30, -0.05, uv.y + n * 0.18));

  /* The band: a diagonal rising out of the lower-left, warped by the flow.
     d is the signed distance across it — large above, small below. */
  float diag = uv.y * 1.25 + (1.0 - uv.x) * 0.42;
  float d = diag + n * 0.30 - uLift;

  /* A single source below the lower-right corner, so the bloom has an origin.
     Anchored in uv space and only aspect-corrected on the horizontal, so a tall
     phone viewport still gets the bloom instead of pushing it off frame. */
  vec2 srcUv = vec2(0.86, 0.02);
  float src = mix(0.22, 1.0,
    smoothstep(1.25, 0.04, length((uv - srcUv) * vec2(aspect, 1.0))));

  float band = smoothstep(1.06, 0.34, d) * src;   // the broad bloom
  float edge = smoothstep(0.66, 0.46, d) * smoothstep(0.24, 0.44, d) * src;  // its crest

  /* Colour walks the bloom stops. The scalar is expanded so the ramp actually
     traverses its whole range inside one frame — otherwise every pixel lands in
     the same two stops and the field reads as one flat hue. uHue carries the
     field from the teal/peach of the opening to the iris/amber of the close. */
  float g = smoothstep(-0.95, 0.85, n * 1.45 + q.x * 0.55 + uHue * 0.75);
  vec3 c = ramp(g, uIris, uCyan, uPeach, uAmber);

  /* Pulled off full saturation. At full strength the arc reads as a laser
     rather than as light on a surface. */
  float lum = dot(c, vec3(0.2126, 0.7152, 0.0722));
  c = mix(vec3(lum), c, 0.70);

  /* Squaring concentrates the bloom into the lower band instead of hazing the
     whole frame with it */
  vec3 col = mix(base, c, clamp(band * band * uBloom * 0.90, 0.0, 1.0));

  /* The crest — the reference's blown highlight along the arc. It is biased up
     the ramp so the warm stops actually appear somewhere in the frame; without
     this the whole field sits in the cool half and reads as one hue. */
  vec3 hot = ramp(clamp(g + 0.40, 0.0, 1.0), uIris, uCyan, uPeach, uAmber);
  col += hot * edge * 0.34 * uBloom;

  /* Past the crest the ground closes back down into the base again, which is
     what gives the arc an edge to be an edge of */
  float mass = smoothstep(0.44, -0.10, d);
  col = mix(col, uAbyss, mass * 0.72);

  /* A faint counter-glow high on the opposite side keeps the quiet half of the
     frame from going dead flat */
  float halo = smoothstep(1.15, 0.05, distance(p, vec2(0.26 * aspect, 0.30)));
  col += c * halo * smoothstep(-0.30, 0.85, n) * 0.055 * uBloom;

  /* Vignette keeps the corners quiet so type stays readable over them */
  float vig = length((uv - 0.5) * vec2(1.06, 1.0));
  col *= 1.0 - 0.44 * vig * vig;

  col = mix(uAbyss, col, uOpacity);
  col += (ign(gl_FragCoord.xy) - 0.5) / 255.0 * 1.6;

  outColor = vec4(col, 1.0);
}`;
